// AdMobCapacitorProvider: native AdMob ads for Capacitor Android/iOS builds.
//
// Mirrors the ad-provider contract used by AdManager (init / interstitial /
// rewarded) and adds optional banner control. The native plugin
// (@capacitor-community/admob) is imported lazily and only when running on a
// Capacitor native platform, so web builds never touch it. Every call is fully
// guarded: if the plugin is missing or anything throws, it resolves with a
// graceful no-fill instead of breaking gameplay.

// Google's public test ad units — used only when initializeForTesting is set and
// the matching unit id is omitted.
const TEST_UNITS = {
  interstitial: 'ca-app-pub-3940256099942544/1033173712',
  rewarded: 'ca-app-pub-3940256099942544/5224354917',
  banner: 'ca-app-pub-3940256099942544/6300978111',
};

const DEFAULT_TIMEOUT_MS = 20000;

export class AdMobCapacitorProvider {
  constructor(cfg, ctx = {}) {
    this.cfg = cfg || {};
    this.name = 'admobCapacitor';
    this.log = ctx.log || console;
    this.emit = ctx.emit || (() => {});
    this.timeoutMs = Number(this.cfg.timeoutMs) > 0 ? Number(this.cfg.timeoutMs) : DEFAULT_TIMEOUT_MS;
    this._mod = null;       // cached plugin module
    this._ready = null;     // init promise
    this._bannerShown = false;
  }

  // Lazily import the native plugin. Returns the module or null (never throws).
  async _load() {
    if (this._mod) return this._mod;
    try {
      // Literal specifier so app bundlers can resolve the installed plugin.
      // Marked external for the SDK's own CDN build (see build.mjs); only ever
      // reached on Capacitor native, so web never evaluates it.
      const mod = await import('@capacitor-community/admob');
      this._mod = mod;
      return mod;
    } catch (err) {
      this.log.warn('[ads:admob] @capacitor-community/admob not available', err);
      return null;
    }
  }

  _adId(kind) {
    const map = { interstitial: this.cfg.interstitialUnitId, rewarded: this.cfg.rewardedUnitId, banner: this.cfg.bannerUnitId };
    return map[kind] || (this.cfg.initializeForTesting ? TEST_UNITS[kind] : null);
  }

  async init() {
    if (this._ready) return this._ready;
    this._ready = (async () => {
      const mod = await this._load();
      if (!mod?.AdMob) return { ok: false, reason: 'not-available' };
      try {
        await mod.AdMob.initialize({
          initializeForTesting: !!this.cfg.initializeForTesting,
        });
        this.log.debug('[ads:admob] initialized', { testing: !!this.cfg.initializeForTesting });
        return { ok: true };
      } catch (err) {
        this.log.warn('[ads:admob] initialize failed', err);
        return { ok: false, reason: 'error' };
      }
    })();
    return this._ready;
  }

  async interstitial(placement = 'default') {
    const adId = this._adId('interstitial');
    if (!adId) return { shown: false, reason: 'not-configured' };
    const ready = await this.init();
    if (!ready?.ok) return { shown: false, reason: ready?.reason || 'not-available' };
    const { AdMob, InterstitialAdPluginEvents } = this._mod;

    let opened = false;
    const result = await this._withListeners(async (track, finish) => {
      track(await AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, () => finish({ shown: false, reason: 'no-fill' })));
      track(await AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, () => finish({ shown: false, reason: 'error' })));
      track(await AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => finish({ shown: true })));
      this.log.debug('[ads:admob] interstitial prepare/show', { placement });
      await AdMob.prepareInterstitial({ adId });
      await AdMob.showInterstitial();
      opened = true;
      this.emit('ad:open', { placement, kind: 'interstitial' });
    }, { shown: false, reason: 'no-fill' });
    if (opened) this.emit('ad:close', { placement, kind: 'interstitial' });
    return result;
  }

  async rewarded(placement = 'default') {
    const adId = this._adId('rewarded');
    if (!adId) return { shown: false, granted: false, reason: 'not-configured' };
    const ready = await this.init();
    if (!ready?.ok) return { shown: false, granted: false, reason: ready?.reason || 'not-available' };
    const { AdMob, RewardAdPluginEvents } = this._mod;

    let granted = false;
    let opened = false;
    const result = await this._withListeners(async (track, finish) => {
      track(await AdMob.addListener(RewardAdPluginEvents.FailedToLoad, () => finish({ shown: false, granted: false, reason: 'no-fill' })));
      track(await AdMob.addListener(RewardAdPluginEvents.FailedToShow, () => finish({ shown: false, granted: false, reason: 'error' })));
      track(await AdMob.addListener(RewardAdPluginEvents.Rewarded, () => { granted = true; }));
      track(await AdMob.addListener(RewardAdPluginEvents.Dismissed, () => finish({ shown: true, granted })));
      this.log.debug('[ads:admob] rewarded prepare/show', { placement });
      await AdMob.prepareRewardVideoAd({ adId });
      await AdMob.showRewardVideoAd();
      opened = true;
      this.emit('ad:open', { placement, kind: 'rewarded' });
    }, { shown: false, granted: false, reason: 'no-fill' });
    if (opened) this.emit('ad:close', { placement, kind: 'rewarded', granted });
    return result;
  }

  async showBanner() {
    const adId = this._adId('banner');
    if (!adId) return { shown: false, reason: 'not-configured' };
    const ready = await this.init();
    if (!ready?.ok) return { shown: false, reason: ready?.reason || 'not-available' };
    try {
      const { AdMob, BannerAdSize, BannerAdPosition } = this._mod;
      await AdMob.showBanner({
        adId,
        adSize: BannerAdSize?.ADAPTIVE_BANNER ?? 'ADAPTIVE_BANNER',
        position: BannerAdPosition?.BOTTOM_CENTER ?? 'BOTTOM_CENTER',
        margin: 0,
      });
      this._bannerShown = true;
      this.log.debug('[ads:admob] banner shown');
      return { shown: true };
    } catch (err) {
      this.log.warn('[ads:admob] showBanner failed', err);
      return { shown: false, reason: 'error' };
    }
  }

  async hideBanner() {
    if (!this._mod?.AdMob || !this._bannerShown) return { hidden: false };
    try {
      await this._mod.AdMob.hideBanner();
      this._bannerShown = false;
      this.log.debug('[ads:admob] banner hidden');
      return { hidden: true };
    } catch (err) {
      this.log.warn('[ads:admob] hideBanner failed', err);
      return { hidden: false, reason: 'error' };
    }
  }

  // Runs `run(track, finish)`, guaranteeing listener cleanup, a single
  // resolution, a timeout fallback, and that nothing throws into gameplay.
  _withListeners(run, timeoutResult) {
    return new Promise((resolve) => {
      let settled = false;
      const listeners = [];
      const cleanup = () => { for (const l of listeners) { try { l?.remove?.(); } catch { /* ignore */ } } };
      const finish = (result) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        cleanup();
        resolve(result);
      };
      const track = (l) => { if (l) listeners.push(l); };
      const timer = setTimeout(() => finish(timeoutResult), this.timeoutMs);
      Promise.resolve(run(track, finish)).catch((err) => {
        this.log.warn('[ads:admob] ad flow error', err);
        finish({ ...timeoutResult, reason: 'error' });
      });
    });
  }
}
